import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import EventCard from "@/components/EventCard";
import VenueCard from "@/components/VenueCard";
import { usePublicSettings } from "@/lib/settings";

const HERO_IMG =
  "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

function SectionHeader({ eyebrow, title, to }) {
  return (
    <div className="flex items-end justify-between mb-6">
      <div>
        <span className="section-eyebrow">{eyebrow}</span>
        <h2 className="font-display text-3xl sm:text-4xl uppercase text-white mt-2 leading-none">{title}</h2>
      </div>
      {to && (
        <Link to={to} data-testid={`view-all-${title.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center gap-1 font-oswald text-xs font-semibold tracking-widest text-zinc-300 hover:text-[#D4AF37] whitespace-nowrap">
          VIEW ALL <ArrowRight size={14} />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [email, setEmail] = useState("");
  const [joining, setJoining] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/events/featured").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/venues").then((r) => setVenues(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const joinList = async (e) => {
    e.preventDefault();
    if (!email) return;
    setJoining(true);
    try {
      await api.post("/list/join", { email, source: "Homepage" });
      toast.success("You're on the ABE list!");
      setEmail("");
    } catch (err) {
      toast.error("Could not sign up. Try again.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        <img src={HERO_IMG} alt="Nightlife crowd" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 hero-fade" />
        <div className="relative mx-auto max-w-6xl w-full px-4 sm:px-6 pb-16 sm:pb-24">
          <div className="max-w-2xl fade-up">
            <p className="font-oswald text-sm font-semibold tracking-[0.3em] text-zinc-200 mb-3">HUNTSVILLE, AL</p>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase leading-[0.85] text-white">
              EVENTS<br />PEOPLE
            </h1>
            <p className="font-script text-5xl sm:text-6xl lg:text-7xl text-[#D4AF37] -mt-1 sm:-mt-2">Talk About</p>
            <p className="mt-5 font-oswald text-xs sm:text-sm tracking-[0.2em] text-zinc-300">
              MUSIC &nbsp;|&nbsp; CULTURE &nbsp;|&nbsp; GOOD PEOPLE &nbsp;|&nbsp; GREAT VENUES
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="hero-upcoming-btn" onClick={() => navigate("/events")} className="btn-gold inline-flex items-center gap-2 rounded-md px-6 py-3.5 font-oswald text-sm tracking-widest">
                UPCOMING EVENTS <ArrowRight size={16} />
              </button>
              <button data-testid="hero-about-btn" onClick={() => navigate("/about")} className="inline-flex items-center rounded-md border border-white/25 px-6 py-3.5 font-oswald text-sm tracking-widest text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                ABOUT ABE
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* UPCOMING */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-14">
        <SectionHeader eyebrow="Featured Events" title="Upcoming Events" to="/events" />
        {events.length === 0 ? (
          <div className="rounded-xl border border-[#27272A] p-10 text-center">
            <p className="font-display text-2xl text-white uppercase">No Upcoming Events</p>
            <p className="text-zinc-400 mt-1 text-sm">New events are coming soon.</p>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            {events.map((ev) => (
              <EventCard key={ev.id} event={ev} className="min-w-[75%] xs:min-w-[62%] sm:min-w-[300px] snap-start" />
            ))}
          </div>
        )}
      </section>

      {/* VENUES */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-16">
        <SectionHeader eyebrow="Experiences Across Huntsville" title="Our Venues" to="/venues" />
        {venues.length > 0 && (
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 md:grid md:grid-cols-3 md:overflow-visible">
            {venues.map((v) => (
              <div key={v.id} className="min-w-[70%] sm:min-w-[280px] md:min-w-0 snap-start">
                <VenueCard venue={v} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ABE LIST */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-16">
        <div className="rounded-2xl border border-[#27272A] bg-gradient-to-br from-[#141417] to-[#09090B] p-8 sm:p-12">
          <h2 className="font-display text-3xl sm:text-4xl uppercase text-white leading-none">Join the ABE List</h2>
          <p className="mt-3 text-zinc-400 max-w-md text-sm">
            Be the first to know about upcoming events, exclusive tickets and special offers.
          </p>
          <form onSubmit={joinList} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-lg">
            <input
              data-testid="abe-list-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              className="flex-1 rounded-md bg-[#1A1A1F] border border-[#27272A] px-4 py-3.5 text-white placeholder-zinc-500 outline-none focus:border-[#D4AF37]"
            />
            <button data-testid="abe-list-submit" disabled={joining} className="btn-gold rounded-md px-8 py-3.5 font-oswald text-sm tracking-widest disabled:opacity-60">
              {joining ? "..." : "SUBSCRIBE"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
